import {
  API_ENDPOINT,
  fetchPsiPayload,
  parsePsiData,
  type PsiData,
  type PsiApiPayload,
} from './psi.ts';

export interface TrendPoint {
  timestamp: string;
  psi: number;
}
export interface TrendData {
  points: TrendPoint[];
  partial: boolean;
}
const DAY = 24 * 60 * 60 * 1000;

export function singaporeDate(time: number): string {
  return new Date(time + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export function parseTrend(payloads: PsiApiPayload[], now: number): TrendPoint[] {
  const points = new Map<number, TrendPoint>();
  for (const payload of payloads) {
    for (const item of payload.data?.items ?? []) {
      try {
        const reading = parsePsiData({ code: payload.code, data: { items: [item] } });
        const time = reading.timestamp ? Date.parse(reading.timestamp) : NaN;
        if (reading.timestamp && time >= now - DAY && time <= now) {
          points.set(time, { timestamp: reading.timestamp, psi: reading.reading.psi });
        }
      } catch {
        /* A missing hour must not discard the remaining history. */
      }
    }
  }
  return [...points.entries()].sort(([a], [b]) => a - b).map(([, point]) => point);
}

export async function fetchPsiTrend(now: number, signal?: AbortSignal): Promise<TrendData> {
  const dates = [...new Set([singaporeDate(now - DAY), singaporeDate(now)])];
  const results = await Promise.allSettled(
    dates.map((date) => fetchPsiPayload(`${API_ENDPOINT}?date=${date}`, signal, 'low')),
  );
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const payloads = results.flatMap((result) =>
    result.status === 'fulfilled' ? [result.value] : [],
  );
  if (!payloads.length) throw new Error('Historical readings are unavailable.');
  return {
    points: parseTrend(payloads, now),
    partial: results.some((result) => result.status === 'rejected'),
  };
}

export function mergeLatestReading(
  points: TrendPoint[],
  reading: PsiData | null,
  now: number,
): TrendPoint[] {
  const byTime = new Map(points.map((point) => [Date.parse(point.timestamp), point]));
  if (reading?.timestamp)
    byTime.set(Date.parse(reading.timestamp), {
      timestamp: reading.timestamp,
      psi: reading.reading.psi,
    });
  return [...byTime.values()]
    .filter(
      (point) => Date.parse(point.timestamp) >= now - DAY && Date.parse(point.timestamp) <= now,
    )
    .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));
}

export function describeTrend(points: TrendPoint[]): { label: string; delta: number | null } {
  const first = points[0];
  const last = points.at(-1);
  if (
    points.length < 2 ||
    !first ||
    !last ||
    Date.parse(last.timestamp) - Date.parse(first.timestamp) < 60 * 60 * 1000
  ) {
    return { label: 'Not enough history', delta: null };
  }
  const delta = last.psi - first.psi;
  return { label: delta > 2 ? 'Worsening' : delta < -2 ? 'Improving' : 'Steady', delta };
}
